import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'antd/es/select';
import Skeleton from 'antd/es/skeleton';
import type { FormEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import type { InferType } from 'yup';

import { setFormErrors } from 'utils/formHelpers';
import { hasValue } from 'utils/hasValue';

import type { CreatedPayoutDraftDataProduct } from 'api';
import { useGetDisbursementPaymentInstruments } from 'api';
import { selectProductStepSchema as schema } from 'validators/selectProductStep';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { FormError } from '@atoms/FormError/FormError';

import { CancelButton } from '../ConfirmCancelDialog';
import {
  CORRIDOR_SELECT_TEST_ID,
  INSTRUMENT_SELECT_TEST_ID,
  NEXT
} from '../constants';
import { ProductSchemaSelector } from '../fields/ProductSchemaSelector';
import type { StepProps } from '../steps';

interface Inputs extends InferType<typeof schema> {
  base: null;
}

type Props = Omit<StepProps, 'fields'> & {
  fields?: CreatedPayoutDraftDataProduct;
  isFinished: boolean;
};

type FieldName = 'localInstrumentName' | 'corridorId' | 'productSchemaId';

export const SelectProductStep = ({
  onSubmit,
  fields,
  onCancel,
  isFinished
}: Props) => {
  // Refs
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  // Data hooks
  const organisationId = useOrganisationId<number>();
  const {
    data: { data: paymentInstruments } = {},
    isPending: isInstrumentsPending
  } = useGetDisbursementPaymentInstruments(organisationId, {
    query: { refetchOnMount: true }
  });

  // Memos
  const stepData = useMemo(() => {
    if (hasValue(fields)) {
      return {
        corridorId: fields.product_id.toString(),
        localInstrumentName: fields.local_instrument,
        productSchemaId: fields.schema_id.toString()
      };
    }
  }, [fields]);

  // Form watchers
  const { handleSubmit, setError, formState, setValue, control } =
    useForm<Inputs>({
      mode: 'onSubmit',
      resolver: yupResolver(schema),
      defaultValues: stepData
    });

  const localInstrumentName = useWatch({
    control,
    name: 'localInstrumentName'
  });

  const corridorId = useWatch({
    control,
    name: 'corridorId'
  });

  const productSchemaId = useWatch({
    control,
    name: 'productSchemaId'
  });

  const localInstrumentsOptions = useMemo(() => {
    return Array.isArray(paymentInstruments)
      ? paymentInstruments.map((instrument) => ({
          label: instrument.local_instrument_family,
          value: instrument.local_instrument_family
        }))
      : [];
  }, [paymentInstruments]);

  const selectedInstrument = useMemo(() => {
    return paymentInstruments?.find(
      (paymentInstrument) =>
        paymentInstrument.local_instrument_family === localInstrumentName
    );
  }, [paymentInstruments, localInstrumentName]);

  const corridorOptions = useMemo(() => {
    if (!selectedInstrument) return [];

    return selectedInstrument.products.map((product) => ({
      label: product.corridor,
      value: product.id.toString()
    }));
  }, [selectedInstrument]);

  const isLocalInstrumentOptionsDisabled = useMemo(
    () =>
      isFinished ||
      localInstrumentsOptions.length === 0 ||
      localInstrumentsOptions.length === 1,
    [isFinished, localInstrumentsOptions.length]
  );

  // Callbacks
  const setFieldValue = useCallback(
    (fieldName: FieldName, value: string): void => {
      setValue(fieldName, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
    },
    [setValue]
  );

  const onLocalInstrumentChange = useCallback(
    (localInstrument: string) => {
      setFieldValue('localInstrumentName', localInstrument);
      setFieldValue('corridorId', '');
      setFieldValue('productSchemaId', '');
    },
    [setFieldValue]
  );

  const onCorridorChange = useCallback(
    (corridorId: string) => {
      setFieldValue('corridorId', corridorId);
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (!corridorId && corridorOptions.length === 1) {
      onCorridorChange(corridorOptions[0].value);
    }
  }, [corridorId, corridorOptions, onCorridorChange]);

  const onProductSchemaChange = useCallback(
    (productSchemaId: string) => {
      setFieldValue('productSchemaId', productSchemaId);
    },
    [setFieldValue]
  );

  const onError = useCallback(
    (error: any, fields: string[]) => {
      setFormErrors({ error, setError, fields });
    },
    [setError]
  );

  // Effects
  useEffect(() => {
    if (!localInstrumentName && localInstrumentsOptions?.length === 1) {
      setFieldValue('localInstrumentName', localInstrumentsOptions[0].value);
    }
  }, [localInstrumentsOptions, localInstrumentName, setFieldValue]);

  // Handlers
  const onSubmitHandler: FormEventHandler<HTMLFormElement> = async (
    event: any
  ) => {
    try {
      await handleSubmit(async (data) => {
        await onSubmit(data);
      })(event);
    } catch (error: any) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(
        () =>
          setFormErrors({
            error,
            setError,
            fields: Object.keys(schema.fields)
          }),
        50
      );
    }
  };

  return (
    <div className="flex justify-center pb-8">
      <form onSubmit={onSubmitHandler} autoComplete="off" className="w-[90%]">
        <div className="mb-4 flex flex-col">
          <label className="mb-0.5 pl-2 text-xs">Transfer type</label>
          {isInstrumentsPending ? (
            <Skeleton.Input active block />
          ) : (
            <Controller
              name="localInstrumentName"
              control={control}
              rules={{ required: 'Transfer type is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={localInstrumentsOptions}
                  placeholder="Select transfer type"
                  disabled={isLocalInstrumentOptionsDisabled}
                  data-testid={INSTRUMENT_SELECT_TEST_ID}
                  onChange={onLocalInstrumentChange}
                  value={field.value}
                />
              )}
            />
          )}
        </div>
        <div className="mb-4 flex flex-col">
          <label className="mb-0.5 pl-2 text-xs">Corridor</label>
          <Controller
            name="corridorId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={corridorId}
                options={corridorOptions}
                onChange={onCorridorChange}
                data-testid={CORRIDOR_SELECT_TEST_ID}
                disabled={
                  isFinished ||
                  corridorOptions.length === 0 ||
                  corridorOptions.length === 1
                }
              />
            )}
          />
        </div>
        <div className="mb-10 flex flex-col">
          <Controller
            name="productSchemaId"
            control={control}
            render={({ fieldState }) => (
              <ProductSchemaSelector
                corridorId={corridorId}
                value={productSchemaId}
                isActive={isFinished}
                onError={onError}
                onChange={onProductSchemaChange}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        <div className="flex gap-4">
          <CancelButton onAction={onCancel} />
          <PrimaryButton
            size="large"
            htmlType="submit"
            className="w-full"
            disabled={
              isFinished || !formState.isValid || formState.isSubmitSuccessful
            }
          >
            {NEXT}
          </PrimaryButton>
        </div>
        <FormError message={formState.errors.base?.message} />
      </form>
    </div>
  );
};
